import { forwardRef, InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

const baseInputStyles =
  'w-full px-4 py-2.5 md:px-6 md:py-4 bg-gray-50 border border-gray-200 rounded-xl md:rounded-2xl focus:ring-4 focus:ring-[#4f6cff]/10 focus:border-[#4f6cff] outline-none transition-all text-base md:text-lg';

// Label Component
interface LabelProps {
  htmlFor: string;
  required?: boolean | undefined;
  children: ReactNode;
  className?: string;
}

export const Label = ({ htmlFor, required, children, className }: LabelProps) => (
  <label htmlFor={htmlFor} className={cn('block text-sm font-semibold md:font-bold text-gray-700 mb-1.5 md:mb-3', className)}>
    {children}
    {required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

// Text Input
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | undefined;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, id, name, required, className, ...props }, ref) => {
    const inputId = id ?? name;

    return (
      <div>
        {label && (
          <Label htmlFor={inputId!} required={required ?? false}>
            {label}
          </Label>
        )}
        <input
          ref={ref}
          id={inputId}
          name={name}
          required={required}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          className={cn(baseInputStyles, error && 'border-red-500 focus:border-red-500', className)}
          {...props}
        />
        {error && (
          <p id={`${inputId}-error`} className="mt-2 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

// Textarea
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string | undefined;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, id, name, required, className, ...props }, ref) => {
    const inputId = id ?? name;

    return (
      <div>
        {label && (
          <Label htmlFor={inputId!} required={required ?? false}>
            {label}
          </Label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          name={name}
          required={required}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          className={cn(baseInputStyles, 'resize-none', error && 'border-red-500 focus:border-red-500', className)}
          {...props}
        />
        {error && (
          <p id={`${inputId}-error`} className="mt-2 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

// Select
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string | undefined;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, id, name, required, options, placeholder, className, ...props }, ref) => {
    const inputId = id ?? name;

    return (
      <div>
        {label && (
          <Label htmlFor={inputId!} required={required ?? false}>
            {label}
          </Label>
        )}
        <select
          ref={ref}
          id={inputId}
          name={name}
          required={required}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          className={cn(
            baseInputStyles,
            'appearance-none',
            error && 'border-red-500 focus:border-red-500',
            className
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && (
          <p id={`${inputId}-error`} className="mt-2 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

// Form Field wrapper for consistent spacing
interface FormFieldProps {
  children: ReactNode;
  className?: string;
  span?: 1 | 2;
}

export const FormField = ({ children, className, span = 1 }: FormFieldProps) => (
  <div className={cn(span === 2 && 'col-span-2', className)}>{children}</div>
);
